import { BehaviorSubject, Observable, take } from 'rxjs';
import { Socket } from 'socket.io-client';

import { SocketEventType, SocketMessage } from '.';

export class SocketImpl {
  constructor(private _socket: Socket) {
    // Register onAny to capture custom events (emitted by the server)
    this._socket.onAny((eventName, ...args) => {
      this._eventPipe.next({ eventName, message: args[0] as SocketMessage });
    });

    // Handle socket connection events on the io manager
    this.handleSocketErrors();
  }

  private _eventPipe = new BehaviorSubject<{ eventName: SocketEventType; message: SocketMessage }>({
    eventName: '' as SocketEventType,
    message: {} as SocketMessage,
  });
  private _connectionStatusPipe = new BehaviorSubject<boolean>(false);

  private get socket() {
    return this._socket;
  }

  events$ = this._eventPipe.asObservable();
  connectionStatus$ = this._connectionStatusPipe.asObservable();

  isConnected() {
    return this._socket.connected;
  }

  send(eventName: SocketEventType, message: SocketMessage) {
    this.socket.emit(eventName, message);
  }

  /**
   * AK-476 (WP-F5): in cookie-session mode the socket authenticates with a
   * one-shot 60s ticket carried as a query param. socket.io reuses the original
   * query on auto-reconnect, but the ticket is single-use — so on every
   * disconnect fetch a fresh ticket and swap it into the manager's query before
   * the reconnect delay elapses.
   */
  refreshTicketOnReconnect(ticketProvider: () => Observable<string | null>) {
    this.socket.on('disconnect', () => {
      ticketProvider()
        .pipe(take(1))
        .subscribe((ticket) => {
          if (ticket) {
            console.debug('[Socket] refreshed ticket after disconnect');
            this.socket.io.opts.query = {
              ...(this.socket.io.opts.query as Record<string, unknown>),
              ticket,
            };
          } else {
            console.debug('[Socket] ticket refresh returned none; socket stays down');
          }
        });
    });
  }

  close() {
    this.socket.disconnect();
    this.socket.removeAllListeners();
    // Complete observables to allow garbage collection and signal subscribers
    this._eventPipe.complete();
    this._connectionStatusPipe.complete();
  }

  private handleSocketErrors() {
    // Listen for successful connection
    this.socket.on('connect', () => {
      console.debug('[Socket] connected', this.socket.id);
      this._connectionStatusPipe.next(true);
    });

    // Listen for disconnection
    this.socket.on('disconnect', (reason: string) => {
      console.debug('[Socket] disconnected:', reason);
      this._connectionStatusPipe.next(false);
    });

    // Listen for connection errors
    this.socket.on('connect_error', (error: Error) => {
      console.debug('[Socket] connect error:', error);
      this._connectionStatusPipe.next(false);
    });

    this.socket.io.on('error', (error: Error) => {
      console.debug(`[Socket] error: ${error.name} - ${error.message}`);
    });

    this.socket.io.on('reconnect_attempt', (attempt: number) => {
      console.debug('[Socket] reconnect attempt', attempt);
    });

    this.socket.io.on('reconnect_error', () => {
      console.debug('[Socket] reconnect error');
    });

    this.socket.io.on('reconnect_failed', () => {
      console.debug('[Socket] reconnect failed after max attempts');
    });

    this.socket.io.on('reconnect', (attempt: number) => {
      console.debug('[Socket] reconnected after', attempt, 'attempt(s)');
    });
  }
}
